import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_V1, fetchApi, getSelectedBranchId } from '../config/api';
import { notifyNewPayment, uploadPaymentProof } from '../config/firebase';
import { getStoredBranch } from './BranchSelectModal';
import { buildCheckoutCartLines } from '../utils/buildCheckoutCartLines';
import { etiquetaGrupoRolls, formatCombinacionSummary } from '../constants/comboCombinaciones';
import { formatWasabiGingerSummary } from '../constants/wasabiGinger';
import {
  loadCheckoutState,
  saveCheckoutState,
  clearCheckoutState,
  sanitizeDeliveryDataForStorage,
  parseMapsQuoteFromSearchParams,
  hasMapsReturnParams,
} from '../utils/checkoutStorage';
import { saveOrderSuccessNotice } from '../utils/orderSuccessNotice';
import './Checkout.css';
import logo from '../assets/Isologo/Isologo_ChefMargaro_2022.png';
import rollDinamitaImage from '../assets/Tipos de Rolls/ROLL Camaron - Dinamita.png';

const NAME_MAX = 150;
const EMAIL_MAX = 150;
const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 11;
const CEDULA_MIN = 6;
const CEDULA_MAX = 12;
const REFERENCE_POINT_MAX = 500;
const REFERENCIA_MIN_DIGITS = 6;
const REFERENCIA_MAX_DIGITS = 12;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_REGEX = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+(?:[ '\u2019.-][A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+)*$/;
const PHONE_REGEX = /^0(?:412|414|416|422|424|426|2\d{2})\d{7}$/;

function normalizeClientValue(name, value) {
  if (name === 'cedula') {
    return String(value).replace(/\D/g, '').slice(0, CEDULA_MAX);
  }
  if (name === 'telefono') {
    return String(value).replace(/\D/g, '').slice(0, PHONE_MAX_DIGITS);
  }
  if (name === 'nombreCompleto') {
    return String(value).slice(0, NAME_MAX);
  }
  if (name === 'email') {
    return String(value).slice(0, EMAIL_MAX);
  }
  return value;
}

function validateNombre(value) {
  const nombre = String(value).trim();
  if (!nombre) return 'El nombre completo es obligatorio.';
  if (nombre.length < 3) return 'El nombre debe tener al menos 3 caracteres.';
  if (nombre.length > NAME_MAX) return `El nombre admite máximo ${NAME_MAX} caracteres.`;
  if (!NAME_REGEX.test(nombre)) return 'El nombre solo puede contener letras y espacios.';
  if (nombre.split(/\s+/).length < 2) return 'Ingresa nombre y apellido.';
  return '';
}

function validateEmail(value) {
  const email = String(value).trim();
  if (!email) return 'El correo electrónico es obligatorio.';
  if (email.length > EMAIL_MAX) return `El correo admite máximo ${EMAIL_MAX} caracteres.`;
  if (!EMAIL_REGEX.test(email)) return 'Ingresa un correo electrónico válido.';
  return '';
}

function validateTelefono(value) {
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return 'El teléfono es obligatorio.';
  if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
    return 'Ingresa un teléfono de 10 u 11 dígitos.';
  }
  const normalized = digits.length === 10 ? `0${digits}` : digits;
  if (!PHONE_REGEX.test(normalized)) {
    return 'Ingresa un teléfono venezolano válido (ejemplo: 04121234567).';
  }
  return '';
}

function validateCedula(value) {
  const ced = String(value).replace(/\D/g, '');
  if (!ced) return 'La cédula es obligatoria.';
  if (ced.length < CEDULA_MIN || ced.length > CEDULA_MAX) {
    return `La cédula debe tener entre ${CEDULA_MIN} y ${CEDULA_MAX} dígitos.`;
  }
  return '';
}

function validatePuntoReferencia(value) {
  const text = String(value).trim();
  if (!text) return '';
  if (text.length < 3) return 'Escribe al menos 3 caracteres o déjalo vacío.';
  if (text.length > REFERENCE_POINT_MAX) {
    return `El punto de referencia admite máximo ${REFERENCE_POINT_MAX} caracteres.`;
  }
  return '';
}

function validateNumeroReferencia(value) {
  const ref = String(value).replace(/\D/g, '');
  if (!ref) return 'El número de referencia es obligatorio para pago móvil.';
  if (ref.length < REFERENCIA_MIN_DIGITS || ref.length > REFERENCIA_MAX_DIGITS) {
    return `El número de referencia debe tener entre ${REFERENCIA_MIN_DIGITS} y ${REFERENCIA_MAX_DIGITS} dígitos.`;
  }
  return '';
}

function clientFieldError(name, value) {
  if (name === 'nombreCompleto') return validateNombre(value);
  if (name === 'email') return validateEmail(value);
  if (name === 'telefono') return validateTelefono(value);
  if (name === 'cedula') return validateCedula(value);
  return '';
}

function messageFromApiBody(body, fallback) {
  if (body?.errors && typeof body.errors === 'object') {
    const details = Object.values(body.errors).flat().filter(Boolean);
    if (details.length) return details.join(' ');
  }
  return body?.message || fallback;
}

async function compressProofImage(file) {
  if (!file || !String(file.type || '').startsWith('image/')) return file;
  if (file.type === 'image/gif') return file;
  if (file.size < 900000 && file.type === 'image/jpeg') return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  if (bitmap.close) bitmap.close();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
  if (!blob) return file;
  return new File([blob], 'comprobante.jpg', { type: 'image/jpeg' });
}
const MAPS_APP_URL = 'https://map.chefmargaro.com/';

function appendFormValue(formData, key, value) {
  if (value === undefined || value === null) {
    return;
  }
  if (value instanceof File || value instanceof Blob) {
    formData.append(key, value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendFormValue(formData, `${key}[${index}]`, item);
    });
    return;
  }
  if (typeof value === 'object') {
    Object.entries(value).forEach(([childKey, childValue]) => {
      appendFormValue(formData, `${key}[${childKey}]`, childValue);
    });
    return;
  }
  if (typeof value === 'boolean') {
    formData.append(key, value ? '1' : '0');
    return;
  }
  formData.append(key, String(value));
}

const parseStepFromParams = (searchParams) => {
  const parsed = parseInt(searchParams.get('step') || '1', 10);
  return parsed >= 1 && parsed <= 3 ? parsed : 1;
};

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const [step, setStep] = useState(() => parseStepFromParams(searchParams));
  const hydratedRef = useRef(false);
  const [formData, setFormData] = useState({
    nombreCompleto: '',
    email: '',
    telefono: '',
    cedula: ''
  });
  const [clientErrors, setClientErrors] = useState({
    nombreCompleto: '',
    email: '',
    telefono: '',
    cedula: ''
  });
  const [deliveryData, setDeliveryData] = useState({
    tipoEntrega: 'delivery',
    direccion: '',
    puntoReferencia: '',
    mapsQuote: null,
  });
  const [deliveryErrors, setDeliveryErrors] = useState({
    direccion: '',
    puntoReferencia: ''
  });
  const [paymentData, setPaymentData] = useState({
    metodoPago: 'pagoMovil',
    comprobante: null,
    numeroReferencia: '',
    imagenBillete: null
  });
  const [paymentErrors, setPaymentErrors] = useState({
    comprobante: '',
    imagenBillete: '',
    numeroReferencia: ''
  });
  const [previewComprobante, setPreviewComprobante] = useState(null);
  const [previewBillete, setPreviewBillete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bcvEurValue, setBcvEurValue] = useState(null);
  const [bcvLoading, setBcvLoading] = useState(() => parseStepFromParams(searchParams) === 3);
  const [modalData, setModalData] = useState({
    open: false,
    type: 'success', // 'success' | 'error'
    title: '',
    message: '',
    trackingLink: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = normalizeClientValue(name, value);
    setFormData(prev => ({
      ...prev,
      [name]: next
    }));
  };

  const validateClientField = (name, value) => {
    setClientErrors(prev => ({
      ...prev,
      [name]: clientFieldError(name, value)
    }));
  };

  const handleClientKeyUp = (e) => {
    const { name, value } = e.target;
    validateClientField(name, normalizeClientValue(name, value));
  };

  const handleDeliveryChange = (e) => {
    const { name, value } = e.target;
    const next = name === 'puntoReferencia'
      ? value.slice(0, REFERENCE_POINT_MAX)
      : value;
    setDeliveryData(prev => ({
      ...prev,
      [name]: next
    }));
  };

  const handleDeliveryKeyUp = (e) => {
    const { name, value } = e.target;
    if (name !== 'puntoReferencia') return;
    setDeliveryErrors(prev => ({
      ...prev,
      puntoReferencia: validatePuntoReferencia(value)
    }));
  };

  const goToStep = useCallback(
    (newStep) => {
      const safeStep = newStep >= 1 && newStep <= 3 ? newStep : 1;
      if (safeStep === 3) {
        setBcvLoading(true);
      }
      setStep(safeStep);
      setSearchParams({ step: String(safeStep) }, { replace: true });
    },
    [setSearchParams]
  );

  const handleTipoEntregaChange = (tipo) => {
    setDeliveryData(prev => ({
      ...prev,
      tipoEntrega: tipo,
      ...(tipo === 'pickup'
        ? { direccion: '', mapsQuote: null }
        : {}),
    }));
    if (tipo === 'pickup') {
      setDeliveryErrors(prev => ({ ...prev, direccion: '', puntoReferencia: '' }));
    }
  };

  const validateClientStep = () => {
    const errors = {
      nombreCompleto: clientFieldError('nombreCompleto', formData.nombreCompleto),
      email: clientFieldError('email', formData.email),
      telefono: clientFieldError('telefono', formData.telefono),
      cedula: clientFieldError('cedula', formData.cedula)
    };

    setClientErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleClientSubmit = (e) => {
    e.preventDefault();
    if (!validateClientStep()) {
      return;
    }
    goToStep(2);
  };

  const handleDeliverySubmit = (e) => {
    e.preventDefault();

    if (deliveryData.tipoEntrega === 'delivery') {
      const dirError = !deliveryData.mapsQuote
        ? 'Debes seleccionar tu dirección en el mapa.'
        : '';
      const refError = validatePuntoReferencia(deliveryData.puntoReferencia);
      setDeliveryErrors(prev => ({ ...prev, direccion: dirError, puntoReferencia: refError }));
      if (dirError || refError) return;
    }
    goToStep(3);
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    if (name === 'numeroReferencia') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, REFERENCIA_MAX_DIGITS);
      setPaymentData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
      return;
    }
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMetodoPagoChange = (metodo) => {
    setPaymentData(prev => ({
      ...prev,
      metodoPago: metodo,
      comprobante: null,
      numeroReferencia: '',
      imagenBillete: null
    }));
    setPaymentErrors({
      comprobante: '',
      imagenBillete: '',
      numeroReferencia: ''
    });
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      if (type === 'comprobante') {
        setPaymentErrors(prev => ({
          ...prev,
          comprobante: 'Debes adjuntar una imagen del comprobante (JPG, PNG, etc.).'
        }));
        e.target.value = '';
      } else if (type === 'billete') {
        setPaymentErrors(prev => ({
          ...prev,
          imagenBillete: 'Debes adjuntar una imagen del billete (JPG, PNG, etc.).'
        }));
        e.target.value = '';
      }
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    if (type === 'comprobante') {
      setPaymentData(prev => ({ ...prev, comprobante: file }));
      setPaymentErrors(prev => ({ ...prev, comprobante: '' }));
      if (previewComprobante) URL.revokeObjectURL(previewComprobante);
      setPreviewComprobante(previewUrl);
    } else if (type === 'billete') {
      setPaymentData(prev => ({ ...prev, imagenBillete: file }));
      setPaymentErrors(prev => ({ ...prev, imagenBillete: '' }));
      if (previewBillete) URL.revokeObjectURL(previewBillete);
      setPreviewBillete(previewUrl);
    }
  };

  // Limpiar URLs de previsualización al desmontar el componente
  useEffect(() => {
    return () => {
      if (previewComprobante) {
        URL.revokeObjectURL(previewComprobante);
      }
      if (previewBillete) {
        URL.revokeObjectURL(previewBillete);
      }
    };
  }, [previewComprobante, previewBillete]);

  // Sin carrito solo redirigir si no es retorno del mapa ni checkout guardado
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      return;
    }
    if (hasMapsReturnParams(searchParams)) {
      return;
    }
    const saved = loadCheckoutState();
    if (saved?.formData || (saved?.step && saved.step >= 2)) {
      return;
    }
    navigate('/', { replace: true });
  }, [cartItems, navigate, searchParams]);

  // Restaurar localStorage y procesar retorno desde mapas (una sola vez al montar)
  useEffect(() => {
    const saved = loadCheckoutState();

    if (saved?.formData) {
      setFormData(saved.formData);
    }
    if (saved?.deliveryData) {
      const { mapsQuote: _mq, direccion: _dir, ...deliveryWithoutQuote } = saved.deliveryData;
      setDeliveryData(prev => ({
        ...prev,
        ...deliveryWithoutQuote,
        mapsQuote: null,
        direccion: '',
      }));
    }
    if (saved?.paymentData) {
      setPaymentData(prev => ({
        ...prev,
        metodoPago: saved.paymentData.metodoPago ?? prev.metodoPago,
        numeroReferencia: saved.paymentData.numeroReferencia ?? '',
      }));
    }

    if (hasMapsReturnParams(searchParams)) {
      const mapsQuote = parseMapsQuoteFromSearchParams(searchParams);
      if (mapsQuote) {
        setDeliveryData(prev => ({
          ...prev,
          tipoEntrega: 'delivery',
          mapsQuote,
          direccion: `${mapsQuote.lat}, ${mapsQuote.lng}`,
        }));
        setDeliveryErrors(prev => ({ ...prev, direccion: '' }));
        setStep(2);
        setSearchParams({ step: '2' }, { replace: true });
      }
    } else if (saved?.step) {
      const savedStep = saved.step >= 1 && saved.step <= 3 ? saved.step : 1;
      if (!searchParams.get('step')) {
        setStep(savedStep);
        setSearchParams({ step: String(savedStep) }, { replace: true });
      }
    }

    const tipoEntrega = saved?.deliveryData?.tipoEntrega ?? 'delivery';
    const urlStep = parseStepFromParams(searchParams);
    if (
      tipoEntrega === 'delivery' &&
      !hasMapsReturnParams(searchParams) &&
      urlStep === 3
    ) {
      setStep(2);
      setSearchParams({ step: '2' }, { replace: true });
    }

    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);

  // Sincronizar paso con ?step= en la URL
  useEffect(() => {
    if (!hydratedRef.current) {
      return;
    }
    if (!searchParams.get('step')) {
      setSearchParams({ step: String(step) }, { replace: true });
      return;
    }
    const urlStep = parseStepFromParams(searchParams);
    if (urlStep !== step) {
      setStep(urlStep);
    }
  }, [searchParams, step, setSearchParams]);

  // Sin cotización de mapa no se puede estar en el paso 3 con delivery
  useEffect(() => {
    if (!hydratedRef.current) {
      return;
    }
    if (
      deliveryData.tipoEntrega === 'delivery' &&
      !deliveryData.mapsQuote &&
      step === 3
    ) {
      goToStep(2);
    }
  }, [deliveryData.tipoEntrega, deliveryData.mapsQuote, step, goToStep]);

  // Persistir datos del checkout (sin archivos de pago ni cotización del mapa)
  useEffect(() => {
    if (!hydratedRef.current) {
      return;
    }
    saveCheckoutState({
      formData,
      deliveryData: sanitizeDeliveryDataForStorage(deliveryData),
      paymentData: {
        metodoPago: paymentData.metodoPago,
        numeroReferencia: paymentData.numeroReferencia,
      },
      step,
    });
  }, [formData, deliveryData, paymentData.metodoPago, paymentData.numeroReferencia, step]);

  const validatePaymentField = (name, value, metodoPago, comprobante, imagenBillete) => {
    let error = '';
    if (name === 'numeroReferencia' && metodoPago === 'pagoMovil') {
      error = validateNumeroReferencia(value);
    }
    if (name === 'comprobante') {
      if ((metodoPago === 'transferencia' || metodoPago === 'pagoMovil') && !comprobante) {
        error = 'Debes adjuntar la imagen del comprobante de pago.';
      }
    }
    if (name === 'imagenBillete') {
      if (metodoPago === 'efectivo' && !imagenBillete) {
        error = 'Debes adjuntar la imagen del billete.';
      }
    }
    setPaymentErrors(prev => ({ ...prev, [name]: error }));
  };

  const validatePaymentStep = () => {
    const { metodoPago, comprobante, imagenBillete, numeroReferencia } = paymentData;
    const errors = {
      comprobante: '',
      imagenBillete: '',
      numeroReferencia: ''
    };

    if (metodoPago === 'transferencia' || metodoPago === 'pagoMovil') {
      if (!comprobante) {
        errors.comprobante = 'Debes adjuntar la imagen del comprobante de pago.';
      }
    }
    if (metodoPago === 'efectivo' && !imagenBillete) {
      errors.imagenBillete = 'Debes adjuntar la imagen del billete.';
    }
    if (metodoPago === 'pagoMovil') {
      errors.numeroReferencia = validateNumeroReferencia(numeroReferencia);
    }

    setPaymentErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handlePaymentKeyUp = (e) => {
    const { name, value } = e.target;
    validatePaymentField(name, value, paymentData.metodoPago, paymentData.comprobante, paymentData.imagenBillete);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    // Guard clause to prevent submitting an empty cart
    if (!cartItems || cartItems.length === 0) {
      return;
    }

    // Prevent multiple rapid submissions
    if (isSubmitting) {
      return;
    }

    if (!validatePaymentStep()) {
      return;
    }

    if (bcvLoading || bcvEurValue == null || !Number.isFinite(bcvEurValue)) {
      return;
    }

    if (
      deliveryData.tipoEntrega === 'delivery' &&
      !deliveryData.mapsQuote
    ) {
      goToStep(2);
      setDeliveryErrors(prev => ({
        ...prev,
        direccion: 'Debes seleccionar tu dirección en el mapa.',
      }));
      return;
    }

    setIsSubmitting(true);

    try {
      const { metodoPago, comprobante, imagenBillete, numeroReferencia } = paymentData;

      const branchId = getSelectedBranchId();
      if (!branchId) {
        alert('Error: No hay sucursal seleccionada. Por favor, selecciona una sucursal.');
        setIsSubmitting(false);
        return;
      }

      const customerEmail = formData.email.trim() || `cliente_${formData.cedula}@temp.com`;
      const cartSubtotal = parseFloat(getTotalPrice());
      const deliveryFee =
        deliveryData.tipoEntrega === 'delivery' && deliveryData.mapsQuote
          ? Number(deliveryData.mapsQuote.total) || 0
          : 0;
      const totalAmount = cartSubtotal + deliveryFee;

      const checkoutPayload = {
        branch_id: branchId,
        customer: {
          name: formData.nombreCompleto.trim(),
          email: customerEmail,
          phone: formData.telefono?.trim() || '',
          cedula: formData.cedula?.trim() || '',
        },
        delivery_type: deliveryData.tipoEntrega,
        delivery_address:
          deliveryData.tipoEntrega === 'delivery' ? deliveryData.direccion.trim() : '',
        reference_point: deliveryData.puntoReferencia?.trim() || '',
        notes: '',
        cart_subtotal: cartSubtotal,
        shipping_cost: deliveryFee,
        total_amount: totalAmount,
        cart_lines: buildCheckoutCartLines(cartItems),
      };

      let paymentMethod = 'cash';
      if (metodoPago === 'transferencia') {
        paymentMethod = 'bank_transfer';
      } else if (metodoPago === 'pagoMovil') {
        paymentMethod = 'mobile_payment';
      }

      const proofSource = metodoPago === 'efectivo' ? imagenBillete : comprobante;
      const proofFile = await compressProofImage(proofSource);
      const branchName = getStoredBranch()?.name || '';

      const formDataBody = new FormData();
      Object.entries(checkoutPayload).forEach(([key, value]) => {
        appendFormValue(formDataBody, key, value);
      });
      formDataBody.append('payment_method', paymentMethod);
      if (metodoPago === 'pagoMovil' && numeroReferencia && numeroReferencia.trim()) {
        formDataBody.append('reference_number', numeroReferencia.trim());
      }
      let proofUrl = null;
      if (proofFile) {
        try {
          proofUrl = await uploadPaymentProof(proofFile);
        } catch (uploadError) {
          console.error('No se pudo subir el comprobante a Firebase Storage:', uploadError);
        }
      }
      if (proofUrl) {
        formDataBody.append('proof_image_url', proofUrl);
      } else if (proofFile) {
        formDataBody.append('proof_image', proofFile);
      }

      const orderResponse = await fetchApi(`${API_V1}/checkout`, {
        method: 'POST',
        body: formDataBody,
      });

      const orderResult = await orderResponse.json().catch(() => ({}));

      if (!orderResponse.ok) {
        throw new Error(messageFromApiBody(orderResult, 'Error al crear la orden y el pago'));
      }

      const orderData = orderResult.data || {};
      const paymentList = Array.isArray(orderData.payments)
        ? orderData.payments
        : Array.isArray(orderData.payments?.data)
          ? orderData.payments.data
          : [];
      const createdPayment = paymentList[0] || null;
      try {
        await notifyNewPayment({
          paymentId: createdPayment?.payment_id,
          orderId: orderData.order_id || orderData.id,
        });
      } catch (notifyError) {
        console.error('No se pudo avisar el pago en tiempo real:', notifyError);
      }

      const sedeText = branchName ? ` Sede: ${branchName}.` : '';
      saveOrderSuccessNotice({
        message: `Tu orden se realizó exitosamente.${sedeText}`,
        trackingLink: orderResult.data?.tracking_link || null,
      });
      clearCart();
      clearCheckoutState();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error al procesar la orden:', error);
      setModalData({
        open: true,
        type: 'error',
        title: 'Error al procesar la orden',
        message: error.message || 'Ocurrió un error al procesar la orden.',
        trackingLink: null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 3) {
      goToStep(2);
    } else if (step === 2) {
      goToStep(1);
    }
  };

  const handleBackToPreviousPage = () => {
    navigate(-1);
  };

  const closeModal = () => {
    setModalData({
      open: false,
      type: 'success',
      title: '',
      message: '',
      trackingLink: null,
    });
  };

  useEffect(() => {
    if (!modalData.open) {
      return undefined;
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalData.open]);

  useEffect(() => {
    if (step !== 3) {
      return undefined;
    }

    let cancelled = false;

    const loadBcvRate = async () => {
      setBcvLoading(true);
      try {
        const response = await fetchApi(`${API_V1}/bcv`);
        const result = await response.json();
        if (cancelled) return;

        if (response.ok && result.success && result.data?.eur_value != null) {
          setBcvEurValue(Number(result.data.eur_value));
        } else {
          setBcvEurValue(null);
        }
      } catch {
        if (!cancelled) {
          setBcvEurValue(null);
        }
      } finally {
        if (!cancelled) {
          setBcvLoading(false);
        }
      }
    };

    loadBcvRate();

    return () => {
      cancelled = true;
    };
  }, [step]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado al portapapeles');
  };

  const subtotal = parseFloat(getTotalPrice());
  const deliveryFee =
    deliveryData.tipoEntrega === 'delivery' && deliveryData.mapsQuote
      ? Number(deliveryData.mapsQuote.total) || 0
      : 0;
  const total = subtotal + deliveryFee;

  const mapsAppUrl = formData.email.trim()
    ? `${MAPS_APP_URL}?email=${encodeURIComponent(formData.email.trim())}`
    : null;

  const montoBolivares =
    bcvEurValue != null && Number.isFinite(bcvEurValue)
      ? total * bcvEurValue
      : null;

  const montoBolivaresNumeros =
    montoBolivares != null
      ? montoBolivares.toLocaleString('es-VE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : null;

  const montoBolivaresFormatted =
    montoBolivaresNumeros != null ? `Bs. ${montoBolivaresNumeros}` : null;

  const showDeliveryRow =
    deliveryData.tipoEntrega === 'delivery' && deliveryData.mapsQuote;

  const renderOrderTotalsBreakdown = (variant = 'sidebar') => (
    <div
      className={
        variant === 'step3'
          ? 'checkout-totals checkout-totals-step3'
          : 'checkout-totals'
      }
    >
      <div className="checkout-subtotal">
        <span className="checkout-total-label">Subtotal</span>
        <span className="checkout-total-value">${subtotal.toFixed(2)}</span>
      </div>
      {showDeliveryRow && (
        <div className="checkout-delivery-fee">
          <span className="checkout-total-label">Delivery</span>
          <span className="checkout-total-value">${deliveryFee.toFixed(2)}</span>
        </div>
      )}
      <div className="checkout-total">
        <span className="checkout-total-label-large">Total</span>
        <span className="checkout-total-value-large">${total.toFixed(2)}</span>
      </div>
    </div>
  );

  const renderMontoBolivaresRow = () => (
    <div className="payment-detail-item">
      <span className="payment-detail-label">Monto en bolívares:</span>
      <span className="payment-detail-value">
        {bcvLoading
          ? 'Cargando tasa…'
          : montoBolivaresFormatted ?? '—'}
      </span>
      {montoBolivaresFormatted && (
        <button
          type="button"
          className="payment-copy-btn"
          onClick={() => copyToClipboard(montoBolivaresNumeros)}
          aria-label="Copiar monto en bolívares"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
      )}
    </div>
  );

  // Do not render checkout when cart is empty (additional guard)
  if (!cartItems || cartItems.length === 0) {
    return null;
  }

  return (
    <div className="checkout-container">
      {modalData.open && (
        <div
          className="checkout-modal-overlay"
          role="presentation"
          onClick={modalData.type === 'error' ? closeModal : undefined}
        >
          <div
            className="checkout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="checkout-modal-close"
              onClick={closeModal}
              aria-label="Cerrar"
            >
              ×
            </button>
            <h3
              id="checkout-modal-title"
              className={`checkout-modal-title ${modalData.type}`}
            >
              {modalData.title}
            </h3>
            <div className="checkout-modal-body">
              {modalData.type === 'success' && (
                <>
                  <p>Serás redirigido al detalle de tu orden.</p>
                  {modalData.message && <p>{modalData.message}</p>}
                </>
              )}
              {modalData.type === 'error' && (
                <p>{modalData.message || 'Ocurrió un error al procesar la orden. Intenta nuevamente.'}</p>
              )}
            </div>
            <div className="checkout-modal-actions">
              {modalData.type === 'success' && modalData.trackingLink && (
                <button
                  type="button"
                  className="checkout-modal-btn checkout-modal-btn-primary"
                  onClick={() => {
                    window.location.href = modalData.trackingLink;
                  }}
                >
                  Ir al tracking
                </button>
              )}
              {modalData.type === 'error' && (
                <button
                  type="button"
                  className="checkout-modal-btn checkout-modal-btn-primary"
                  onClick={closeModal}
                >
                  Cerrar
                </button>
              )}
              {modalData.type === 'success' && !modalData.trackingLink && (
                <button
                  type="button"
                  className="checkout-modal-btn"
                  onClick={closeModal}
                >
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Header con logo */}
      <div className="checkout-header">
        <div className="checkout-header-inner">
          <button
            type="button"
            className="checkout-header-back-btn"
            onClick={handleBackToPreviousPage}
            aria-label="Volver a la página anterior"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <div className="checkout-logo">
            <div className="checkout-logo-circle">
              <img src={logo} alt="Logo" className="checkout-logo-image" />
            </div>
            <span className="checkout-logo-text">MARGARO</span>
          </div>
        </div>
      </div>

      <div className="checkout-content">
        {/* Sección izquierda - Formulario */}
        <div className="checkout-left">
          {step === 1 ? (
            <>
              {/* Indicador de progreso */}
              <div className="checkout-progress">
                <div className="progress-dots">
                  <span className="progress-dot active"></span>
                  <span className="progress-dot"></span>
                  <span className="progress-dot"></span>
                </div>
                <span className="progress-text">1/3 pasos</span>
              </div>

              <h2 className="checkout-section-title">Datos del cliente</h2>

              <form onSubmit={handleClientSubmit} className="checkout-form" noValidate>
                <div className="form-field">
                  <label htmlFor="nombreCompleto" className="form-label">
                    Nombre completo*
                  </label>
                  <input
                    type="text"
                    id="nombreCompleto"
                    name="nombreCompleto"
                    value={formData.nombreCompleto}
                    onChange={handleChange}
                    onKeyUp={handleClientKeyUp}
                    autoComplete="name"
                    maxLength={NAME_MAX}
                    aria-invalid={clientErrors.nombreCompleto ? 'true' : 'false'}
                    className={`form-input ${clientErrors.nombreCompleto ? 'input-error' : ''}`}
                  />
                  {clientErrors.nombreCompleto && (
                    <p className="form-error">{clientErrors.nombreCompleto}</p>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="email" className="form-label">
                    Correo electrónico*
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onKeyUp={handleClientKeyUp}
                    autoComplete="email"
                    maxLength={EMAIL_MAX}
                    aria-invalid={clientErrors.email ? 'true' : 'false'}
                    className={`form-input ${clientErrors.email ? 'input-error' : ''}`}
                  />
                  {clientErrors.email && (
                    <p className="form-error">{clientErrors.email}</p>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="telefono" className="form-label">
                    Teléfono / WhatsApp*
                  </label>
                  <input
                    type="text"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    onKeyUp={handleClientKeyUp}
                    autoComplete="tel"
                    inputMode="numeric"
                    maxLength={PHONE_MAX_DIGITS}
                    aria-invalid={clientErrors.telefono ? 'true' : 'false'}
                    className={`form-input ${clientErrors.telefono ? 'input-error' : ''}`}
                  />
                  {clientErrors.telefono && (
                    <p className="form-error">{clientErrors.telefono}</p>
                  )}
                </div>

                <div className="form-field">
                  <label htmlFor="cedula" className="form-label">
                    Cédula*
                  </label>
                  <input
                    type="text"
                    id="cedula"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleChange}
                    onKeyUp={handleClientKeyUp}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={CEDULA_MAX}
                    aria-invalid={clientErrors.cedula ? 'true' : 'false'}
                    className={`form-input ${clientErrors.cedula ? 'input-error' : ''}`}
                  />
                  {clientErrors.cedula && (
                    <p className="form-error">{clientErrors.cedula}</p>
                  )}
                </div>

                <button type="submit" className="checkout-next-btn">
                  Siguiente
                </button>
              </form>

              <p className="checkout-delivery-info">
                Tiempos de entrega: 30 min - 60 min.
              </p>
            </>
          ) : (step === 2 ? (
            <>
              {/* Indicador de progreso con flechas */}
              <div className="checkout-progress">
                <button 
                  className="progress-arrow-btn"
                  onClick={handleBack}
                  aria-label="Volver"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <div className="progress-dots">
                  <span className="progress-dot active"></span>
                  <span className="progress-dot active"></span>
                  <span className="progress-dot"></span>
                </div>
                <button 
                  className="progress-arrow-btn"
                  disabled
                  aria-label="Siguiente"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
                <span className="progress-text">2/3 pasos</span>
              </div>

              <h2 className="checkout-section-title">Datos de entrega</h2>

              <form onSubmit={handleDeliverySubmit} className="checkout-form">
                {/* Opciones de entrega */}
                <div className="delivery-options">
                  <div 
                    className={`delivery-option ${deliveryData.tipoEntrega === 'delivery' ? 'selected' : ''}`}
                    onClick={() => handleTipoEntregaChange('delivery')}
                  >
                    <div className="delivery-option-checkbox">
                      {deliveryData.tipoEntrega === 'delivery' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span className={`delivery-option-label ${deliveryData.tipoEntrega === 'delivery' ? 'active' : ''}`}>
                      Delivery
                    </span>
                  </div>

                  <div 
                    className={`delivery-option ${deliveryData.tipoEntrega === 'pickup' ? 'selected' : ''}`}
                    onClick={() => handleTipoEntregaChange('pickup')}
                  >
                    <div className="delivery-option-checkbox">
                      {deliveryData.tipoEntrega === 'pickup' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span className={`delivery-option-label ${deliveryData.tipoEntrega === 'pickup' ? 'active' : ''}`}>
                      Pickup
                    </span>
                  </div>
                </div>

                {deliveryData.tipoEntrega === 'delivery' && (
                  <>
                    <div className="form-field">
                      <span className="form-label">Dirección*</span>
                      {mapsAppUrl ? (
                        <a
                          href={mapsAppUrl}
                          className="checkout-maps-btn"
                        >
                          Seleccionar dirección en el mapa
                        </a>
                      ) : (
                        <p className="form-error">
                          Completa el e-mail en el paso 1 para abrir el mapa.
                        </p>
                      )}
                      {deliveryData.mapsQuote && (
                        <div className="checkout-maps-summary">
                          <p className="checkout-maps-summary-line">
                            Costo delivery: Ref {deliveryData.mapsQuote.total.toFixed(2)}
                          </p>
                          {deliveryData.mapsQuote.restriction && (
                            <p className="checkout-maps-summary-restriction">
                              {deliveryData.mapsQuote.restriction}
                            </p>
                          )}
                        </div>
                      )}
                      {deliveryErrors.direccion && (
                        <p className="form-error">{deliveryErrors.direccion}</p>
                      )}
                    </div>

                    <div className="form-field">
                      <label htmlFor="puntoReferencia" className="form-label">
                        Punto de referencia (opcional)
                      </label>
                      <input
                        type="text"
                        id="puntoReferencia"
                        name="puntoReferencia"
                        value={deliveryData.puntoReferencia}
                        onChange={handleDeliveryChange}
                        onKeyUp={handleDeliveryKeyUp}
                        maxLength={REFERENCE_POINT_MAX}
                        aria-invalid={deliveryErrors.puntoReferencia ? 'true' : 'false'}
                        className={`form-input ${deliveryErrors.puntoReferencia ? 'input-error' : ''}`}
                        placeholder="Punto de referencia (opcional)"
                      />
                      {deliveryErrors.puntoReferencia && (
                        <p className="form-error">{deliveryErrors.puntoReferencia}</p>
                      )}
                    </div>
                  </>
                )}

                <button type="submit" className="checkout-next-btn">
                  Siguiente
                </button>
                <button type="button" className="checkout-back-btn" onClick={handleBack}>
                  Volver
                </button>
              </form>

              <p className="checkout-delivery-info">
                Tiempos de entrega: 30 min - 60 min.
              </p>
            </>
          ) : (
            <>
              {/* Indicador de progreso con flechas - Paso 3 */}
              <div className="checkout-progress">
                <button 
                  className="progress-arrow-btn"
                  onClick={handleBack}
                  aria-label="Volver"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
                <div className="progress-dots">
                  <span className="progress-dot active"></span>
                  <span className="progress-dot active"></span>
                  <span className="progress-dot active"></span>
                </div>
                <button 
                  className="progress-arrow-btn"
                  disabled
                  aria-label="Siguiente"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
                <span className="progress-text">3/3 pasos</span>
              </div>

              <h2 className="checkout-section-title">Datos del pago</h2>

              {renderOrderTotalsBreakdown('step3')}

              <form onSubmit={handlePaymentSubmit} className="checkout-form" noValidate>
                {/* Opciones de método de pago */}
                <div className="payment-options">
                  <div 
                    className={`payment-option ${paymentData.metodoPago === 'efectivo' ? 'selected' : ''}`}
                    onClick={() => handleMetodoPagoChange('efectivo')}
                  >
                    <div className="payment-option-checkbox">
                      {paymentData.metodoPago === 'efectivo' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span className={`payment-option-label ${paymentData.metodoPago === 'efectivo' ? 'active' : ''}`}>
                      Efectivo
                    </span>
                  </div>

                  <div 
                    className={`payment-option ${paymentData.metodoPago === 'transferencia' ? 'selected' : ''}`}
                    onClick={() => handleMetodoPagoChange('transferencia')}
                  >
                    <div className="payment-option-checkbox">
                      {paymentData.metodoPago === 'transferencia' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span className={`payment-option-label ${paymentData.metodoPago === 'transferencia' ? 'active' : ''}`}>
                      Transferencia bancaria
                    </span>
                  </div>

                  <div 
                    className={`payment-option ${paymentData.metodoPago === 'pagoMovil' ? 'selected' : ''}`}
                    onClick={() => handleMetodoPagoChange('pagoMovil')}
                  >
                    <div className="payment-option-checkbox">
                      {paymentData.metodoPago === 'pagoMovil' && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span className={`payment-option-label ${paymentData.metodoPago === 'pagoMovil' ? 'active' : ''}`}>
                      Pago móvil
                    </span>
                  </div>
                </div>

                {/* Datos de pago según el método seleccionado */}
                {paymentData.metodoPago === 'transferencia' && (
                  <div className="payment-details-section">
                    <h3 className="payment-details-title">Datos de pago</h3>
                    <div className="payment-details-list">
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Banco:</span>
                        <span className="payment-detail-value">Banco de Venezuela</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard('Banco de Venezuela')}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">RIF:</span>
                        <span className="payment-detail-value">J-123456789-0</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard('J-123456789-0')}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Teléfono:</span>
                        <span className="payment-detail-value">0412-1234567</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard('0412-1234567')}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Monto:</span>
                        <span className="payment-detail-value">${total.toFixed(2)}</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard(total.toFixed(2))}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      {renderMontoBolivaresRow()}
                    </div>
                  </div>
                )}

                {paymentData.metodoPago === 'pagoMovil' && (
                  <div className="payment-details-section">
                    <h3 className="payment-details-title">Datos de pago</h3>
                    <div className="payment-details-list">
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Banco:</span>
                        <span className="payment-detail-value">Banco de Venezuela</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard('Banco de Venezuela')}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">RIF:</span>
                        <span className="payment-detail-value">J-123456789-0</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard('J-123456789-0')}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Teléfono:</span>
                        <span className="payment-detail-value">0412-1234567</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard('0412-1234567')}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Monto:</span>
                        <span className="payment-detail-value">${total.toFixed(2)}</span>
                        <button 
                          className="payment-copy-btn"
                          onClick={() => copyToClipboard(total.toFixed(2))}
                          aria-label="Copiar"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                          </svg>
                        </button>
                      </div>
                      {renderMontoBolivaresRow()}
                    </div>
                  </div>
                )}

                {/* Adjuntar comprobante */}
                {(paymentData.metodoPago === 'transferencia' || paymentData.metodoPago === 'pagoMovil') && (
                  <div className="form-field">
                    <label className="form-label">Adjuntar comprobante (imagen)*</label>
                    <div className={`file-upload-container ${paymentErrors.comprobante ? 'input-error' : ''}`}>
                      <input
                        type="file"
                        id="comprobante"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'comprobante')}
                        className="file-input"
                      />
                      <label htmlFor="comprobante" className="file-upload-label">
                        <div className="file-upload-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                        <span className="file-upload-text">Adjuntar comprobante</span>
                        {paymentData.comprobante && (
                          <span className="file-upload-name">{paymentData.comprobante.name}</span>
                        )}
                      </label>
                    </div>
                    {paymentErrors.comprobante && (
                      <p className="form-error">{paymentErrors.comprobante}</p>
                    )}
                    {previewComprobante && (
                      <div className="file-preview-container">
                        <img 
                          src={previewComprobante} 
                          alt="Vista previa del comprobante" 
                          className="file-preview-image"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Adjuntar imagen del billete (solo para efectivo) */}
                {paymentData.metodoPago === 'efectivo' && (
                  <div className="form-field">
                    <label className="form-label">Adjuntar imagen del billete*</label>
                    <div className={`file-upload-container ${paymentErrors.imagenBillete ? 'input-error' : ''}`}>
                      <input
                        type="file"
                        id="billete"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'billete')}
                        className="file-input"
                      />
                      <label htmlFor="billete" className="file-upload-label">
                        <div className="file-upload-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                        <span className="file-upload-text">Adjuntar imagen del billete</span>
                        {paymentData.imagenBillete && (
                          <span className="file-upload-name">{paymentData.imagenBillete.name}</span>
                        )}
                      </label>
                    </div>
                    {paymentErrors.imagenBillete && (
                      <p className="form-error">{paymentErrors.imagenBillete}</p>
                    )}
                    {previewBillete && (
                      <div className="file-preview-container">
                        <img 
                          src={previewBillete} 
                          alt="Vista previa del billete" 
                          className="file-preview-image"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Número de referencia (solo para pago móvil) */}
                {paymentData.metodoPago === 'pagoMovil' && (
                  <div className="form-field">
                    <label htmlFor="numeroReferencia" className="form-label">
                      Nro. de referencia* ({REFERENCIA_MIN_DIGITS} a {REFERENCIA_MAX_DIGITS} dígitos)
                    </label>
                    <input
                      type="text"
                      id="numeroReferencia"
                      name="numeroReferencia"
                      value={paymentData.numeroReferencia}
                      onChange={handlePaymentChange}
                      onKeyUp={handlePaymentKeyUp}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={REFERENCIA_MAX_DIGITS}
                      aria-invalid={paymentErrors.numeroReferencia ? 'true' : 'false'}
                      className={`form-input ${paymentErrors.numeroReferencia ? 'input-error' : ''}`}
                      placeholder={`Solo números, de ${REFERENCIA_MIN_DIGITS} a ${REFERENCIA_MAX_DIGITS} dígitos`}
                    />
                    {paymentErrors.numeroReferencia && (
                      <p className="form-error">{paymentErrors.numeroReferencia}</p>
                    )}
                  </div>
                )}

                <div className="checkout-bs-amount">
                  {renderMontoBolivaresRow()}
                </div>
                {!bcvLoading && montoBolivares == null && (
                  <p className="form-error">
                    No se pudo calcular el monto en bolívares. El pago se habilita cuando ese monto cargue.
                  </p>
                )}
                <button
                  type="submit"
                  className="checkout-order-btn"
                  disabled={isSubmitting || bcvLoading || montoBolivares == null}
                >
                  {isSubmitting
                    ? 'Procesando...'
                    : bcvLoading
                      ? 'Cargando monto en bolívares...'
                      : 'Ordenar'}
                </button>
                <button type="button" className="checkout-back-btn" onClick={handleBack}>
                  Volver
                </button>
              </form>

              <p className="checkout-delivery-info">
                Tiempos de entrega: 30 min - 60 min.
              </p>
            </>
          ))}
        </div>

        {/* Sección derecha - Resumen del carrito */}
        <div className="checkout-right">
          <div className="checkout-cart-summary">
            {cartItems.map((item, index) => (
              <div key={`${item.id}-${item.type}`} className="checkout-cart-item">
                <div className="checkout-item-image-container">
                  <img
                    src={item.image || rollDinamitaImage}
                    alt={item.name}
                    className="checkout-item-image"
                  />
                  <span className="checkout-item-number">{index + 1}</span>
                </div>
                <div className="checkout-item-info">
                  <p className="checkout-item-name">{item.name}</p>
                  <p className="checkout-item-details">
                    {item.quantity} {item.quantity === 1 ? 'UNID.' : 'UNID.'}
                  </p>
                  {item.type === 'extra' && item.accompaniments?.length > 0 && (
                    <p className="checkout-combinacion-line">
                      {formatWasabiGingerSummary(item.accompaniments)}
                    </p>
                  )}
                  {item.type === 'combo' && item.combinaciones && item.combinaciones.length > 0 && (
                    <div className="checkout-item-combinaciones">
                      {item.rollsConSesamo !== undefined && (
                        <p className="checkout-combinacion-line checkout-combinacion-sesamo">
                          Sésamo: {item.rollsConSesamo ? 'Sí' : 'No'}
                        </p>
                      )}
                      {item.rollsConQuesoCrema !== undefined && (
                        <p className="checkout-combinacion-line checkout-combinacion-queso">
                          Queso crema: {item.rollsConQuesoCrema ? 'Sí' : 'No'}
                        </p>
                      )}
                      {item.rollsConCebollin !== undefined && (
                        <p className="checkout-combinacion-line checkout-combinacion-queso">
                          Cebollín: {item.rollsConCebollin ? 'Sí' : 'No'}
                        </p>
                      )}
                      {item.rollsConQuesoCrema === undefined &&
                        item.rollsConCebollin === undefined &&
                        item.rollsConQuesoCremaCebollin !== undefined && (
                        <p className="checkout-combinacion-line checkout-combinacion-queso">
                          Queso crema y cebollín:{' '}
                          {item.rollsConQuesoCremaCebollin ? 'Sí' : 'No'}
                        </p>
                      )}
                      {item.combinaciones.map((combi, i) => (
                        <p key={i} className="checkout-combinacion-line">
                          {etiquetaGrupoRolls(i)}: {formatCombinacionSummary(combi)}
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="checkout-item-price">${(parseFloat(item.price) * item.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}

            {renderOrderTotalsBreakdown('sidebar')}
          </div>

          {/* Logo al final */}
          <div className="checkout-brand-logo">
            <div className="checkout-brand-circle">
              <img src={logo} alt="Chef Margaro" className="checkout-brand-image" />
            </div>
            <div className="checkout-brand-text">
              <span className="checkout-brand-chef">CHEF</span>
              <span className="checkout-brand-margaro">MARGARO</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

